import { useState } from 'react';
import { EyeOff, Info } from 'lucide-react';
import { buildStoriesView, STORY_PRIVACY_NOTE } from '../../../lib/reporting/socialContent';
import { ChannelIcon } from '../../../components/ui/ChannelIcon';
import { ImageLightbox } from '../../../components/ui/ImageLightbox';
import type { SocialStory, SocialStoryMetrics } from '../../../types/socialReporting';
import { Section } from './SocialContentTab';

// Content tab — Instagram Stories insights. Stories are ephemeral (24h); Zernio
// auto-subscribes the story_insights webhook on connect, so measured stories serve
// source live→cached and survive expiry. A story whose account was connected AFTER
// it expired is source 'unavailable' → shown as "not captured", NEVER as 0.
// Facebook Stories are publish-only (Meta exposes no FB story insights), so there's
// no FB equivalent here.

// The metrics shown, in display order (the brief's set). tapsForward/Back, exits and
// swipesForward are the navigation breakdown, shown secondary.
const PRIMARY_METRICS: Array<{ key: keyof SocialStoryMetrics; label: string }> = [
  { key: 'views', label: 'Views' },
  { key: 'reach', label: 'Reach' },
  { key: 'replies', label: 'Replies' },
  { key: 'shares', label: 'Shares' },
  { key: 'profileVisits', label: 'Profile visits' },
  { key: 'follows', label: 'Follows' },
  { key: 'totalInteractions', label: 'Interactions' },
];
const NAV_METRICS: Array<{ key: keyof SocialStoryMetrics; label: string }> = [
  { key: 'tapsForward', label: 'Taps forward' },
  { key: 'tapsBack', label: 'Taps back' },
  { key: 'exits', label: 'Exits' },
  { key: 'swipesForward', label: 'Swipes away' },
];

export function StoriesSection({ stories }: { stories: SocialStory[] }) {
  const view = buildStoriesView(stories);
  const [lightbox, setLightbox] = useState<string | null>(null);

  // Ephemeral: with nothing retrievable, don't render an empty shell.
  if (!view.hasAny) return null;

  return (
    <Section title="Instagram Stories" subtitle="story insights (24h ephemeral)">
      <div className="space-y-4">
        {view.measured.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {view.measured.map((story) => (
              <StoryCard key={story.id} story={story} onOpenImage={setLightbox} />
            ))}
          </div>
        ) : (
          <p className="rounded-md bg-slate-50 px-3 py-4 text-center text-sm text-slate-400">
            No story insights captured for this account yet.
          </p>
        )}

        {view.unavailable.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {view.unavailable.map((story) => (
              <UnavailableStory key={story.id} story={story} />
            ))}
          </div>
        )}

        <p className="flex items-center gap-1.5 text-xs text-slate-400">
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {STORY_PRIVACY_NOTE}
        </p>
      </div>

      {lightbox && <ImageLightbox url={lightbox} alt="Story" onClose={() => setLightbox(null)} />}
    </Section>
  );
}

function StoryCard({
  story,
  onOpenImage,
}: {
  story: SocialStory;
  onOpenImage: (url: string) => void;
}) {
  const m = story.metrics;
  return (
    <div className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <StoryThumb story={story} onOpenImage={onOpenImage} />
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-1.5">
          <ChannelIcon channel="instagram" className="h-3.5 w-3.5" />
          <span className="text-xs text-slate-500">{formatWhen(story.postedAt)}</span>
          {story.source === 'cached' && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
              Final
            </span>
          )}
        </div>
        {m && (
          <>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
              {PRIMARY_METRICS.map(({ key, label }) => (
                <MetricRow key={key} label={label} value={m[key]} />
              ))}
            </dl>
            <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-slate-100 pt-1.5">
              {NAV_METRICS.map(({ key, label }) => (
                <MetricRow key={key} label={label} value={m[key]} muted />
              ))}
            </dl>
          </>
        )}
      </div>
    </div>
  );
}

function StoryThumb({
  story,
  onOpenImage,
}: {
  story: SocialStory;
  onOpenImage: (url: string) => void;
}) {
  if (!story.thumbnailUrl) {
    return <div className="h-20 w-14 shrink-0 rounded-md bg-slate-100" />;
  }
  return (
    <button
      type="button"
      onClick={() => onOpenImage(story.thumbnailUrl!)}
      title="View full size"
      className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-slate-100 hover:opacity-90"
    >
      <img src={story.thumbnailUrl} alt="Story" loading="lazy" className="h-full w-full object-cover" />
    </button>
  );
}

function MetricRow({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className={muted ? 'text-[11px] text-slate-400' : 'text-[11px] text-slate-500'}>{label}</dt>
      <dd className={muted ? 'text-xs tabular-nums text-slate-500' : 'text-xs font-medium tabular-nums text-slate-800'}>
        {value.toLocaleString()}
      </dd>
    </div>
  );
}

// An 'unavailable' story: insights were never captured (account connected after the
// 24h expiry). Shown explicitly as "not captured" — NEVER as zeroed metrics.
function UnavailableStory({ story }: { story: SocialStory }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-400">
      <EyeOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>Insights not captured{story.postedAt ? ` · ${formatWhen(story.postedAt)}` : ''}</span>
    </div>
  );
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}
